import { DecimalPipe } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { WindowRefService } from '../../../../services/window-ref.service';
import { ofType } from '@ngrx/effects';
import { ActionsSubject, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { FormTracking } from '../../../../config/eev-form-tracking.config';
import { AuthFormName } from '../../../../enums/event/event-data.enum';
import { EventTrackingService } from '../../../../services/event-tracking.service';
import { INCOME_UPDATE_SUCCESS_DELAY } from '../../config/income.config';
import { IncomeChangeModalData, UpdateStatedIncomeReq } from '../../models/income.model';
import { getUpdateStatedIncomeActions } from '../../../customer-attributes/store/customer-attributes.actions';
import { DLEntry } from '../../../shared/models/description-list-entry.model';

interface AfterClosedState {
  isSuccessfullySubmitted: boolean;
}

@Component({
    selector: 'app-confirm-income-change-modal',
    templateUrl: './confirm-income-change-modal.component.html',
    styleUrls: ['./confirm-income-change-modal.component.scss'],
    standalone: false
})
export class ConfirmIncomeChangeModalComponent implements OnInit {
  isSubmitting = false;
  isErroneouslySubmitted = false;
  isSuccessfullySubmitted = false;
  entries: DLEntry[];
  private readonly ngUnsubscribe$: Subject<boolean> = new Subject();
  incomeFormTrackingConfig = FormTracking[AuthFormName.PROFILE_INCOME_EDIT];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: IncomeChangeModalData,
    public dialogRef: MatDialogRef<ConfirmIncomeChangeModalComponent, AfterClosedState>,
    private readonly store: Store,
    private readonly actionsSubj: ActionsSubject,
    private readonly windowRefService: WindowRefService,
    private readonly decimalPipe: DecimalPipe,
    private readonly eventTrackingService: EventTrackingService
  ) {}

  buildEntries(): DLEntry[] {
    return [
      {
        label: 'Individual Gross Annual Income',
        value: `$${this.decimalPipe.transform(this.data.individualGrossAnnualIncome)}`,
      },
      { label: 'Household Income', value: `$${this.decimalPipe.transform(this.data.householdIncome)}` },
      { label: 'Employment Status', value: this.data.employmentStatus.label },
    ];
  }

  ngOnInit(): void {
    this.entries = this.buildEntries();
    this.actionsSubj
      .pipe(
        takeUntil(this.ngUnsubscribe$),
        ofType(getUpdateStatedIncomeActions.error, getUpdateStatedIncomeActions.success)
      )
      .subscribe((action: any) => {
        this.isSubmitting = false;
        if (action.type === getUpdateStatedIncomeActions.success.type) {
          this.isSuccessfullySubmitted = true;
          setTimeout(() => {
            this.dialogRef.close({ isSuccessfullySubmitted: true });
          }, INCOME_UPDATE_SUCCESS_DELAY);
        } else if (action.type === getUpdateStatedIncomeActions.error.type) {
          this.isErroneouslySubmitted = true;
        }
      });

    this.dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe(() => {
        if (this.isErroneouslySubmitted || this.isSuccessfullySubmitted) {
          // need to reset stale success/error values
          this.store.dispatch(getUpdateStatedIncomeActions.reset());
        }
      });
  }

  onCancel() {
    this.dialogRef.close();
  }

  ngOnDestroy() {
    this.ngUnsubscribe$.next(null);
    this.ngUnsubscribe$.complete();
  }

  getUpdateStatedIncomeReq(): UpdateStatedIncomeReq {
    return {
      customer_provided_income: this.data.individualGrossAnnualIncome,
      customer_provided_household_income: this.data.householdIncome,
      customer_provided_employment_status: this.data.employmentStatus.value,
    };
  }

  updateIncome() {
    this.isSubmitting = true;
    this.isErroneouslySubmitted = false;
    this.windowRefService.nativeWindow.FS.event('[Customer Hub] Submit profile income update');
    this.store.dispatch(getUpdateStatedIncomeActions.start({ request: this.getUpdateStatedIncomeReq() }));
  }
}
